import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Crown } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import OpenBadge from "@/components/OpenBadge";

function BrandWordmark() {
  return (
    <span className="inline-flex items-center gap-2.5" data-testid="brand-wordmark">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-primary/40">
        <Crown className="w-4 h-4 text-primary" strokeWidth={1.5} />
      </span>
      <span className="font-display tracking-tight text-xl leading-none">
        King&rsquo;s <span className="gold-text">Café</span>
      </span>
    </span>
  );
}

export default function PublicNavbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home"), end: true },
    { to: "/about", label: t("nav.about") },
    { to: "/menu", label: t("nav.menu") },
    { to: "/events", label: t("nav.events") },
    { to: "/booking", label: t("nav.booking") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const itemCls = ({ isActive }) =>
    `relative text-sm tracking-wide transition ${
      isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
    }`;

  return (
    <header
      data-testid="public-navbar"
      className={`sticky top-0 z-40 transition-all ${
        scrolled
          ? "bg-background/85 backdrop-blur-md border-b border-border/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="navbar-brand">
          <BrandWordmark />
        </Link>
        <nav className="hidden lg:flex items-center gap-7" data-testid="navbar-links">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={itemCls}
              data-testid={`navlink-${l.to.replace("/", "") || "home"}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <OpenBadge compact />
          <LanguageSwitcher />
        </div>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-foreground"
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div
          className="lg:hidden border-t border-border/60 bg-background/95 backdrop-blur-md"
          data-testid="mobile-menu"
        >
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex flex-col gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `text-base py-2 ${isActive ? "text-primary" : "text-foreground"}`
                }
                data-testid={`mobile-navlink-${l.to.replace("/", "") || "home"}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center justify-between pt-3 border-t border-border/60">
              <OpenBadge compact testid="open-badge-mobile" />
              <LanguageSwitcher testid="lang-switch-mobile" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
